// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { CustomEvent } from "../Utils/CustomEvents.js";
import { CustomErrorClass, ErrorInformationStorage } from "../Commons/ErrorInformationStorage.js";
import { Resources } from "../Commons/properties/Resources.js";
import { ToolsCache } from "../Commons/cache/ToolsCache.js";
import { popupErrorHandler, workspaceManagerHandler } from "../Utils/AppHandlers.js";
import { UserWebStorage, UserWebStorageInstance } from "../user-web-storage.js";
import { DockSystemInstance } from "./DockSystem.js";
import { TerceraPopupBaloon } from "./elements/TerceraPopupBaloon.js";
import { WorkSpace } from "./WorkSpace.js";
import { GeneralSettings } from "../Utils/GeneralSettings/GeneralSettings.js";
import { DataCache } from "../Commons/DataCache.js";
import { TerceraSymbolLookupBaseDataProvider } from "../Commons/NoNFixedListCore.js";
import { MainWindowManager } from "./UtilsClasses/MainWindowManager.js";
import { BrowserUtils } from "../Commons/UtilsClasses/BrowserUtils.js";
import { NewNetTraderMode } from "./screen/NewNetTraderMode.js";
import { ApplicationInfo } from "../Commons/ApplicationInfo.js";


let _WorkSpaceManager = function ()
{
    this.currentWorkspace = null;
    this.wsList = {};// <id>:<name>
    this.workSpaceListChanged = new CustomEvent();
    this.needRestoreWS = false; // при авторелогине

    this.WorkspacesLimitReached = new CustomEvent();

    GeneralSettings.SettingsChanged.Subscribe(
        this.updateSettings, this);

    this.saveIntervalId = null;

    this.LoadedDataWorkSpace = new LoadWorkSpaceResponse();
    this.OnWorkSpaceChanged = new CustomEvent();
};

export const MAX_WORKSPACE_COUNT = 10;

_WorkSpaceManager.prototype.initWorkspaces = function ()
{
    let self = this;
    return UserWebStorageInstance.workspaces.getInitData()
        .then(function (data)
        {
            // TODO. Ugly. Part 1.
            let wsList = self.wsList = {};
            let active_ws_id = data.active_ws_id;
            let ws_data_array = data.ws_data_array;

            // TODO. Ugly. Part 2.
            self.addArrayToWsList(ws_data_array);

            // TODO. Reconnect -> repopulate panels without reloading of the whole ws.
            if (self.needRestoreWS)
                return;

            self.getNewCurrentWs(active_ws_id)

            return true;
        })
        .catch(function ()
        {
            let ex = new CustomErrorClass("WorkSpaceManager error", "WorkSpaceManager.initWorkspaces", "initWorkspaces -> getInitData");
            ErrorInformationStorage.GetException(ex);

            self.LoadedDataWorkSpace = new LoadWorkSpaceResponse();
            self.createWorkSpace();
            return false;
        });
};

_WorkSpaceManager.prototype.saveWorkSpace = function ()
{
    let ws = this.currentWorkspace;
    if (!ws) return Promise.resolve();// Promise.reject('currentWorkspace doesn\'t exist.');

    let reqObj = this.getWsDataForSave(ws);
    if (reqObj)
        return this.saveWorkSpaceOnServer(ws.fileName, reqObj);
    else
        return Promise.resolve();
};

_WorkSpaceManager.prototype.getWsDataForSave = function (ws)
{
    if (!ws) return null;

    let tools = ToolsCache.GetToolsForSave();
    let reqObj = {
        data: ws.serializePanels(),
        name: ws.visibleName,
        locked: ws.locked,
        panels_amount: ws.getPanelsAmount(),
        tools: tools
    };

    return reqObj;
};

_WorkSpaceManager.prototype.saveWorkSpaceOnServer = function (name, reqObj)
{
    if (ApplicationInfo.isExploreMode)
        return Promise.resolve();
    return UserWebStorageInstance.workspaces.update(name, reqObj);
};

_WorkSpaceManager.prototype.setActiveWorkspaceId = function (wsID)
{
    return UserWebStorageInstance.workspaces.setActiveWorkspaceId(wsID);
};

_WorkSpaceManager.prototype.restoreWorkSpace = function (wsData)
{
    let currentWorkspace = this.currentWorkspace;
    if (!currentWorkspace) return;
    try
    {
        ToolsCache.CreateToolsFromSaves(wsData.tools, DataCache);
        currentWorkspace.visibleName = wsData.name;
        currentWorkspace.updateLocked(wsData.locked);
        currentWorkspace.loadPanels(wsData.data);
        currentWorkspace.populate();
    }
    catch (e)       //#99183
    {
        UserWebStorage.ErrorRest();

        try
        {
            let brokenWorkspaceID = currentWorkspace.fileName
            UserWebStorageInstance.workspaces.delete([brokenWorkspaceID]);
            this.deleteFromWsList(brokenWorkspaceID)

            let ex = new CustomErrorClass("WorkSpaceManager", "restoreWorkSpace", "restoreWorkSpace -> loadPanels");
            ErrorInformationStorage.GetException(ex);
            currentWorkspace.allPanels = {};
            DockSystemInstance.clearDocking();
        }
        finally
        {
            UserWebStorageInstance.workspaces.getDefaultData()
                .then(function (defaultData)
                {
                    if (defaultData)
                    {
                        let newActiveID = defaultData.active_ws_id
                        if (newActiveID)
                            this.changeWorkSpace(newActiveID, true);
                    }
                }.bind(this))
                .catch(function (e)
                {
                    this.getNewCurrentWs()  // на случай если дефолтового нет
                }.bind(this))
        }
    }

    DockSystemInstance.dockManager.hideCloseButton(currentWorkspace.locked);

    this.workSpaceListChanged.Raise();
};

_WorkSpaceManager.prototype.RestoreWorkspaces = function ()
{
    if (this.needRestoreWS)
        return Promise.resolve();
    if (this.LoadedDataWorkSpace.IsLoaded)
    {
        if (this.LoadedDataWorkSpace.JsonData)
            this.restoreWorkSpace(this.LoadedDataWorkSpace.JsonData);
        return Promise.resolve();
    }

    return new Promise(function (resolve, reject)
    {
        this.LoadedDataWorkSpace.OnLoadedDelegate = resolve;
    }.bind(this));
}

// TODO. Refactor.
_WorkSpaceManager.prototype.changeWorkSpace = function (wsId, noNeedToSaveOldWS)
{
    let self = this;

    if (self.lastChangeWs)
        self.lastChangeWs.cancel();

    if (!noNeedToSaveOldWS)
        self.saveWorkSpace();

    return self.lastChangeWs =
        Promise.resolve()
            .then(function ()
            {

                self.disposeCurrentWorkspaceObj();
                return self.setActiveWorkspaceId(wsId);
            })
            .then(function ()
            {

                return UserWebStorageInstance.workspaces.get(wsId);
            })
            .then(function (wsData)
            {
                self.createCurrentWorkspaceObj(wsId, self.wsList[wsId]);
                if (wsData.data && wsData.data.insDict)
                {
                    let instrs = wsData.data.insDict;
                    let k = Object.keys(instrs);

                    if (!k.length)
                        return wsData;

                    let insReqArr = new Array();
                    for (let i = 0; i < k.length; i++)
                    {
                        let ins = k[i];
                        let DataProvider = new TerceraSymbolLookupBaseDataProvider();
                        if (ins && !DataCache.getInstrumentByName(ins))
                            insReqArr.push(DataProvider.getInstrumentByName(ins));
                    }
                    //TODO костыль исправить с тикетом 84863
                    DataCache.currentWorkspaceInsDict = instrs || {};

                    if (insReqArr.length)
                        return Promise.all(insReqArr)
                            .then(function (insArr)
                            {
                                return wsData;
                            });
                    else
                        return wsData;
                }
                else
                    return wsData;

            })
            .then(function (wsData)
            {
                if (self.LoadedDataWorkSpace.OnLoadedDelegate) 
                {
                    self.restoreWorkSpace(wsData);
                    self.LoadedDataWorkSpace.OnLoadedDelegate();
                    self.LoadedDataWorkSpace.Clear();
                }
                else if (MainWindowManager.MainWindow.get('netTraderState') === NewNetTraderMode.WorkMode)
                {
                    self.restoreWorkSpace(wsData);
                }
                else
                {
                    self.LoadedDataWorkSpace.IsLoaded = true;
                    self.LoadedDataWorkSpace.JsonData = wsData;
                }
            })
            .then(function ()
            {
                self.OnWorkSpaceChanged.Raise()
            });
};

//-------------------------------- Shieeeeet ---------------------------------//

_WorkSpaceManager.prototype.closeWorkSpace = function (wsId)
{
    if (wsId in this.wsList)
        this.closeWorkspacesWork([wsId]);
};

_WorkSpaceManager.prototype.closeAllWorkSpacesButCurrent = function ()
{
    let curWsId = this.currentWorkspace.fileName;
    let wsList = this.wsList;
    let ids = [];

    for (let wsId in wsList)
        if (wsId !== curWsId)
            ids.push(wsId);

    this.closeWorkspacesWork(ids);
};

_WorkSpaceManager.prototype.closeWorkspacesWork = function (ids)
{
    if (!ids && !ids.length)
        return;

    this.deleteFromWsList(ids);
    if (self.lastChangeWs)
        self.lastChangeWs.cancel();

    UserWebStorageInstance.workspaces.delete(ids);

    // TODO. Ugly.
    let curWs = this.currentWorkspace;
    let curWsId = curWs ? curWs.fileName : null;

    // Current workspace is still here - do nothing.
    if (curWsId in this.wsList)
        return;

    this.disposeCurrentWorkspaceObj();

    this.getNewCurrentWs()
};

//-------------------------------- Shieeeeet ---------------------------------//
_WorkSpaceManager.prototype.AddWorkSpace = function ()
{
    this.saveWorkSpace();
    this.createWorkSpace();
}

_WorkSpaceManager.prototype.createWorkSpace = function ()
{
    let self = this;
    if (self.isWorkspaceLimitReached())
        return;

    if (self.lastChangeWs)
        self.lastChangeWs.cancel();

    let wsId = self.getNewWorkSpaceFileName();
    let wsName = self.getDefaultWorkSpaceName();

    UserWebStorageInstance.workspaces.create(wsId, { name: wsName })
        .then(function ()
        {
            return self.setActiveWorkspaceId(wsId);
        });

    self.LoadedDataWorkSpace.IsLoaded = true;
    self.addToWsList(wsId, wsName);

    self.disposeCurrentWorkspaceObj();
    self.createCurrentWorkspaceObj(wsId, self.wsList[wsId]);
    self.OnWorkSpaceChanged.Raise()
};

_WorkSpaceManager.prototype.cloneCurrentWorkSpace = function ()
{
    let self = this;
    if (self.isWorkspaceLimitReached())
        return;
    var wsList = this.wsList;
    let keys = Object.keys(wsList);
    let clonedWsNameNew = "";
    let dictToFind = {};
    let clonedWsId = self.getNewWorkSpaceFileName();
    let clonedWsName = this.currentWorkspace.visibleName + "-";
    for (let i = 0; i < keys.length; i++)
    {
        dictToFind[wsList[keys[i]]] = keys[i];
    }
    for (let i = 1; i <= MAX_WORKSPACE_COUNT; i++)
    {
        clonedWsNameNew = clonedWsName + i;
        if (!dictToFind[clonedWsNameNew]) break;
    }
    UserWebStorageInstance.workspaces
        .clone(self.currentWorkspace.fileName, clonedWsId)
        .then(function ()
        {
            self.addToWsList(clonedWsId, clonedWsNameNew);
            self.tryChangeWorkSpaceVisibleName(clonedWsId, clonedWsNameNew);
            self.changeWorkSpace(clonedWsId);
        });
};

//-------------------------------- Shieeeeet ---------------------------------//

_WorkSpaceManager.prototype.checkText = function (tb, newName)
{
    if (BrowserUtils.regexpSymbol.test(newName) === true)
    {
        popupErrorHandler.Show(tb,
            Resources.getResource('textBoxWithValidating.ErrorText.NameContainsInvalidChar'),
            Resources.getResource('screen.error.title'), true);
        return true;
    }
    if (BrowserUtils.regexpEnd.test(newName) === true || BrowserUtils.regexpText.test(newName) === true)
    {
        popupErrorHandler.Show(tb,
            Resources.getResource('textBoxWithValidating.ErrorText.NameExist'),
            Resources.getResource('screen.error.title'), true);
        return true;
    }
    popupErrorHandler.Hide(tb);
    return false;
}

_WorkSpaceManager.prototype.tryChangeWorkSpaceVisibleName = function (wsFileName, newName)
{
    if (!(wsFileName in this.wsList))
        return false;

    this.wsList[wsFileName] = newName;

    if (this.currentWorkspace.fileName === wsFileName)
        this.currentWorkspace.visibleName = newName;

    UserWebStorageInstance.workspaces.update(wsFileName, { name: newName });
    return true;
};

_WorkSpaceManager.prototype.clearAndSaveCurrentWorkSpace = function ()
{
    var cw = this.currentWorkspace;
    if (cw)
    {
        cw.clear();
        this.saveWorkSpace();
    }
};

_WorkSpaceManager.prototype.clearCurrentWorkSpace = function ()
{
    var cw = this.currentWorkspace;
    if (cw)
        cw.clear();
    this.currentWorkspace = null;
}

_WorkSpaceManager.prototype.canClearCurrentWorkSpace = function ()
{
    var cw = this.currentWorkspace;
    return cw && cw.getPanelsAmount();
};

//-------------------------------- Shieeeeet ---------------------------------//

_WorkSpaceManager.prototype.disposeCurrentWorkspaceObj = function ()
{
    let ws = this.currentWorkspace;
    if (ws) ws.dispose();
    this.currentWorkspace = null;
    this.workSpaceListChanged.Raise();
};

_WorkSpaceManager.prototype.createCurrentWorkspaceObj = function (wsId, name)
{
    this.currentWorkspace = new WorkSpace(name, wsId);
    this.workSpaceListChanged.Raise();
};

//-------------------------------- Shieeeeet ---------------------------------//

// TODO. Add a workspace order data which is not stored in a filename.
// WorkSpaceManager.prototype.getNewWorkSpaceFileName = function ()
// {
//     var wsList = this.wsList;
//     var filenamePrefix = FILENAME_PREFIX;
//     let key = filenamePrefix;
//     for (let i = 1; i <= WorkSpaceManager.MAX_WORKSPACE_COUNT; i++)
//     {
//         key = filenamePrefix + i;
//         if (!wsList[key]) break;
//     }
//     return key;
// };

_WorkSpaceManager.prototype.getNewWorkSpaceFileName = function ()   // жопоболь: 91022 судя по всему ошибка возникает, когда после удаления единственного врк создаем врк с id Workspace1, но из базы врк еще не удалился -> пока не наведем порядок 84863 такой костылек  
{
    var wsList = this.wsList;
    var filenamePrefix = FILENAME_PREFIX;

    let key;
    do
    {
        key = filenamePrefix + (Math.floor(Math.random() * 100000) + 1);
    } while (wsList[key])

    return key;
};

_WorkSpaceManager.prototype.getDefaultWorkSpaceName = function ()
{
    var wsList = this.wsList;
    var defName = Resources.getResource('workspace.DefaultNameSpace');
    let keys = Object.keys(wsList);
    let dictToFind = {};
    let newDefName = "";
    for (let i = 0; i < keys.length; i++)
    {
        dictToFind[wsList[keys[i]]] = keys[i];
    }
    for (let i = 1; i <= MAX_WORKSPACE_COUNT; i++)
    {
        newDefName = defName + i;
        if (!dictToFind[newDefName]) break;
    }
    return newDefName;
};

export const FILENAME_PREFIX = 'WorkSpace';

_WorkSpaceManager.workSpaceCompare = function (fileName1, fileName2)
{
    var filenamePrefix = FILENAME_PREFIX;

    var number1 = parseInt(fileName1.substring(filenamePrefix.length));
    var number2 = parseInt(fileName2.substring(filenamePrefix.length));

    if (isNaN(number1) || isNaN(number2))
        return 0;

    return number1 - number2;
};

_WorkSpaceManager.prototype.getNewCurrentWs = function (preferredWsId)
{
    let wsList = this.wsList,
        newCurWsId

    if (preferredWsId in wsList)
        newCurWsId = preferredWsId;
    else
    {
        // TODO. Ugly.
        let fileNames = Object.keys(wsList);
        fileNames.sort(_WorkSpaceManager.workSpaceCompare);

        newCurWsId = fileNames[fileNames.length - 1];
    }

    if (newCurWsId)
        this.changeWorkSpace(newCurWsId);
    else
        this.createWorkSpace();
};

_WorkSpaceManager.prototype.isWorkspaceLimitReached = function ()
{
    if (this.wsListLength() >= MAX_WORKSPACE_COUNT)
    {
        this.WorkspacesLimitReached.Raise();
        return true;
    }
    return false;
};

//-------------------------------- Shieeeeet ---------------------------------//

_WorkSpaceManager.prototype.wsListLength = function ()
{
    if (!this.wsList)
        return 0;

    return Object.keys(this.wsList).length;
};

// TODO. Rename.
_WorkSpaceManager.prototype.addArrayToWsList = function (wsDataArray)
{
    if (!wsDataArray || !wsDataArray.length)
        return;

    let wsList = this.wsList;
    for (let i = 0, len = wsDataArray.length; i < len; i++)
    {
        let wsItem = wsDataArray[i];
        wsList[wsItem.ws_id] = wsItem.name;
    }

    this.workSpaceListChanged.Raise();
};

// TODO. Rename.
_WorkSpaceManager.prototype.addToWsList = function (wsId, name)
{
    this.wsList[wsId] = name;
    this.workSpaceListChanged.Raise();
};

// TODO. Rename.
_WorkSpaceManager.prototype.deleteFromWsList = function (wsId)
{
    if (!wsId)
        return;

    let changed = false;
    let wsList = this.wsList;

    if (Array.isArray(wsId))
    {
        for (let i = 0, len = wsId.length; i < len; i++)
        {
            let id = wsId[i];
            if (id in wsList)
            {
                delete wsList[id];
                changed = true;
            }
        }
    }
    else if (wsId in wsList)
    {
        delete wsList[wsId];
        changed = true;
    }

    if (changed)
        this.workSpaceListChanged.Raise();
};


_WorkSpaceManager.prototype.resetCurrentWithDefault = function ()    // создает новый дефолтовый ws, затем удаляется старый активный ws
{
    var brokenWorkspaceID = this.currentWorkspace.fileName,
        wsName = this.currentWorkspace.visibleName

    UserWebStorageInstance.workspaces.getDefaultData()
        .then(function (defaultData)
        {
            if (defaultData)
            {
                let newActiveID = defaultData.active_ws_id
                if (newActiveID)
                    this.changeWorkSpace(newActiveID, true).then(function ()
                    {
                        let cW = this.currentWorkspace
                        if (cW)
                        {
                            cW.visibleName = wsName
                            this.addToWsList(cW.fileName, cW.visibleName)
                        }

                        UserWebStorageInstance.workspaces.delete([brokenWorkspaceID]);
                        this.deleteFromWsList(brokenWorkspaceID)
                    }.bind(this));
            }
        }.bind(this))
        .catch(function (e)
        {
            this.getNewCurrentWs()  // на случай если дефолтового нет
        }.bind(this))
}

//-------------------------------- Shieeeeet ---------------------------------//

_WorkSpaceManager.prototype.toggleLock = function ()
{
    let workspace = this.currentWorkspace;
    if (!workspace) return;
    workspace.locked = !workspace.locked;

    DockSystemInstance.dockManager.hideCloseButton(workspace.locked);
    DockSystemInstance.dockManager.resizeTabHost();

    UserWebStorageInstance.workspaces.update(
        workspace.fileName,
        { locked: workspace.locked });

    return workspace.locked;
};

_WorkSpaceManager.prototype.processUndock = function ()
{
    if (this.currentWorkspace.locked)
    {
        MainWindowManager.MainWindow.Controls.popupBaloon.show(
            TerceraPopupBaloon.States.WorkspaceLocked);
        // Processed.
        return true;
    }
    return false;
};

_WorkSpaceManager.prototype.processDockResize = function ()
{
    if (this.currentWorkspace.locked)
    {
        MainWindowManager.MainWindow.Controls.popupBaloon.show(
            TerceraPopupBaloon.States.WorkspaceLocked);
        // Processed.
        return true;
    }
    return false;
};

_WorkSpaceManager.prototype.processDialogWindowDrag = function ()
{
    return this.currentWorkspace.locked;
};

_WorkSpaceManager.prototype.isDirty = function (wsId)
{
    var currentWorkSpace = this.currentWorkspace;
    return currentWorkSpace &&
        currentWorkSpace.fileName === wsId &&
        currentWorkSpace.isDirty;
};

//-------------------------------- Shieeeeet ---------------------------------//

_WorkSpaceManager.prototype.startSaveLoop = function ()
{
    clearInterval(this.saveIntervalId);
    this.saveIntervalId = setInterval(
        this.saveWorkSpace.bind(this),
        60000);
};

_WorkSpaceManager.prototype.stopSaveLoop = function ()
{
    clearInterval(this.saveIntervalId);
    this.saveIntervalId = null;
};

//-------------------------------- Shieeeeet ---------------------------------//

_WorkSpaceManager.prototype.updateSettings = function ()
{
    if (this.currentWorkspace)
        this.currentWorkspace.updateSettings();
};

_WorkSpaceManager.prototype.populateCurrentWorkspace = function ()
{
    if (this.currentWorkspace)
        this.currentWorkspace.populate();
};

_WorkSpaceManager.prototype.localize = function ()
{
    if (this.currentWorkspace)
        this.currentWorkspace.localize();
};

_WorkSpaceManager.prototype.themeChange = function ()
{
    if (this.currentWorkspace)
        this.currentWorkspace.themeChange();
};

_WorkSpaceManager.prototype.TickAsync = function ()
{
    if (this.currentWorkspace)
        this.currentWorkspace.TickAsync();
};

_WorkSpaceManager.prototype.RefreshPanelsWithInstrumentDescriptions = function (insArray)
{
    if (this.currentWorkspace)
        this.currentWorkspace.RefreshPanelsWithInstrumentDescriptions(insArray);
};

_WorkSpaceManager.numberOfPanelByType = function (panelType) 
{
    return WorkSpaceManager.currentWorkspace.numberOfPanelByType(panelType);
};

export let LoadWorkSpaceResponse = function ()
{
    this.JsonData = null;
    this.IsLoaded = false;
    this.OnLoadedDelegate = null;
}

LoadWorkSpaceResponse.prototype.Clear = function ()
{
    this.JsonData = null;
    this.IsLoaded = false;
    this.OnLoadedDelegate = null;
}

export const WorkSpaceManager = new _WorkSpaceManager();

window.WorkSpaceManager = WorkSpaceManager; //it is necessary to fix reference in dockspawn.js

let wrkmgrHanl = workspaceManagerHandler;
wrkmgrHanl.numberOfPanelByType = _WorkSpaceManager.numberOfPanelByType;
wrkmgrHanl.RefreshPanelsWithInstrumentDescriptions = (insArray) => { return WorkSpaceManager.RefreshPanelsWithInstrumentDescriptions(insArray); }